import { isPlatformServer } from '@angular/common';
import {
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DropdownOptions } from 'src/app/modules/shared/components/shared-button/shared-button.model';
import { ProductCard } from 'src/app/modules/shared/components/shared-product/shared-product-class';
import { FilterChipItem } from 'src/app/modules/shared/models/filter-chip.model';
import { Product } from 'src/app/modules/shared/models/products.model';
import { CountryDetail } from 'src/app/modules/shared/models/profile.mode';
import { PanelService } from 'src/app/modules/shared/services/api/panel.service';
import { GoogleTagManagerService } from 'src/app/modules/shared/services/google-tag-manager.service';
import { PaginationService } from 'src/app/modules/shared/services/pagination/pagination.service';
import { StoreDetailService } from 'src/app/modules/shared/services/resolver/store-detail.service';
import { SeoScriptsService } from 'src/app/modules/shared/services/seo-scripts.service';
import { LocalStorageService } from 'src/app/modules/shared/services/storage/local-storage.service';
import { StoreRegionService } from 'src/app/modules/shared/services/store-region.service';
import { environment } from 'src/environments/environment';

declare var $;

@Component({
  selector: 'app-category',
  templateUrl: './category.component.html',
  styleUrls: ['./category.component.scss'],
})
export class CategoryComponent implements OnInit, OnDestroy {
  productList: ProductCard[];
  categoryId;
  countryId;
  globalId = environment.globalID;
  searchParam;
  isFirstApiCall: boolean = true;
  categoryList: FilterChipItem[];
  brandForm: FormGroup;
  categoryName: string;
  hasCategory: boolean = false;
  resultCount: number;
  nextpage: string;
  rangeSlider = {
    from: 0,
    to: 1000,
  };

  autoTicks = false;
  disabled = false;
  invert = false;
  max = 100;
  min = 0;
  showTicks = false;
  step = 1;
  thumbLabel = false;
  price = 0;
  vertical = false;
  tickInterval = 1;

  sortType = new FormControl(this.activatedRoute.snapshot.queryParams?.sort, [
    Validators.required,
  ]);
  selectedSortLabel: string;
  discountFilter = new FormControl(false, [Validators.required]);
  showMobileMenu: boolean = false;
  @ViewChild('menuCursor') menuCursor: ElementRef;
  @ViewChild('mobileMenu') mobileMenu: ElementRef;
  isMobile: boolean = window.innerWidth < 768 ? true : false;

  response: any;
  pageSizeOptions = [24, 36, 48];
  pageSize = this.pageSizeOptions[0];
  pageOffset: number = 0;

  regionType: FormControl = new FormControl('');
  selected: CountryDetail;
  allLanguages: any = {
    name: 'all',
    image: 'assets/images/icons/allLanguages.jpeg',
  };
  global: any = {
    name: 'Global',
    image: 'assets/images/icons/allLanguages.jpeg',
  };
  storeDetail;
  isServer: boolean = false;
  dropdownOptions: DropdownOptions;
  private sortOptions = [
    {
      label: 'Name A-Z',
      value: 'name',
    },
    {
      label: 'Name Z-A',
      value: '-name',
    },
    {
      label: 'Popular',
      value: 'popularity',
    },
    {
      label: 'Recently Added',
      value: 'created_time',
    },
    {
      label: 'Best Offers',
      value: 'best_offer',
    },
    {
      label: 'Most Popular',
      value: 'most_popular',
    },
  ];

  constructor(
    private renderer: Renderer2,
    private panelService: PanelService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private storeDetailResolver: StoreDetailService,
    private storeRegionService: StoreRegionService,
    private googleTagManagerService: GoogleTagManagerService,
    private localStorageService: LocalStorageService,
    private paginationService: PaginationService,
    private seoScriptService: SeoScriptsService,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isServer = isPlatformServer(platformId);
    this.setSortOptions();
    this.activatedRoute.params.subscribe((params) => {
      this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    });
  }

  private setSortOptions() {
    const data = this.sortOptions.map((i) => ({
      ...i,
      isActive: this.activatedRoute.snapshot.queryParams?.sort === i.value,
    }));

    this.dropdownOptions = {
      data,
      onSelect: this.sortBy.bind(this),
    };
  }

  sortBy(sort: string) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { sort },
      queryParamsHandling: 'merge',
    });
  }

  ngOnInit(): void {
    this.getStoreDetail();
    this.isFirstApiCall = true;
    this.initForm();
    this.setRegionDetail();
    this.formListener();
    this.getCategoryList();
  }

  getStoreDetail() {
    this.storeDetail = this.storeDetailResolver.permissionInit;
  }

  setRegionDetail() {
    if (!this.isServer) {
      let selectedRegion = this.storeRegionService.selectedRegion;
      if (selectedRegion) {
        const countryParam = parseInt(
          this.activatedRoute.snapshot.queryParamMap.get('country')
        );
        if (countryParam !== selectedRegion.id) {
          this.storeRegionService.setRegionById(countryParam);
          selectedRegion = this.storeRegionService.selectedRegion;
        }
        this.regionType.setValue(selectedRegion);
        this.selected = selectedRegion;
        this.setRouteParam();
        this.selectRegion(this.selected);
      }
    }
    if (this.isFirstApiCall) {
      this.getProductList();
    }
  }

  formatLabel(value: number) {
    if (value >= 1000) {
      return Math.round(value / 1000);
    }

    return value;
  }

  setRouteParam() {
    this.activatedRoute.params.subscribe((params) => {
      this.categoryId = params.id ? params.id : '';
    });
    this.activatedRoute.queryParams.subscribe((params) => {
      this.searchParam = params.search ? params.search : '';
      this.countryId = params.country ? params.country : '';
      if (
        !params.search &&
        !params.category &&
        !params.country &&
        !params.global
      ) {
        this.isFirstApiCall = false;
        this.getProductList();
      }

      if (params.search || params.category || params.country || params.global) {
        this.isFirstApiCall = false;
        this.getProductList();
      }
    });
  }

  getCategoryList() {
    const allCategories: FilterChipItem = {
      label: 'All',
      url: '/category/all',
      isActive: !this.categoryId,
      avatar: '/assets/images/icons/Arrow-right-icon.svg',
    };
    this.panelService
      .getCategoryList({ is_collection: false })
      .subscribe((categories) => {
        this.categoryList = [
          allCategories,
          ...categories.map((c) => ({
            label: c.name,
            url: `/category/${c.category_detail.id}/${this.getCategoryUrlFriendlyName(c.name)}`,
            isActive: c.category_detail.id == this.categoryId,
            avatar: c.avatar,
          })),
        ];
        if (this.categoryId) {
          const categoryMetaData = categories.find(
            (category) => category.id === this.categoryId
          ).javascript_metadata;
          this.seoScriptService.addFullScriptForStoreCategory(categoryMetaData);
        }
      });
  }

  selectRegion(region) {
    this.router.navigate([], {
      queryParams: this.getQueryParamByRegion(region.id, false),
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  getPage() {
    if (this.nextpage != null) {
      this.paginationService.fetchNextData(this.nextpage).subscribe((list) => {
        this.nextpage = list.next;
        this.productList = [
          ...this.productList,
          ...this.getValidProductList(list.results),
        ];
      });
      this.nextpage = null;
    }
  }

  selectCategory(category) {
    if (category == 'all') {
      this.categoryName = '';
      this.hasCategory = false;
    } else {
      this.categoryName = category.name;
      this.hasCategory = true;

      let analyticData = {
        name: category?.name,
        id: category.id,
      };
      this.googleTagManagerService.sendAnalytics('view_category', {
        ...analyticData,
      });
    }
    this.showHideMenu();
  }

  getProductList() {
    this.selectedSortLabel = this.sortOptions.filter(
      (s) => s.value === this.sortType.value
    )[0]?.label;
    let data = {
      limit: 20,
      offset: 0,
      ordering: this.sortType.value,
      categories: this.categoryId,
      search: this.searchParam,
      show_countries: this.countryId || this.regionType?.value?.id,
      global_id: this.globalId,
      has_discount: this.discountFilter.value && true,
    };
    if (this.localStorageService.getItem('region') == 'all') {
      delete data.show_countries;
    }

    this.panelService.getProductList(data).subscribe((products) => {
      this.setSortOptions();
      this.response = products;
      this.resultCount = products.count;
      this.nextpage = products.next;
      this.productList = this.getValidProductList(products.results);
      this.paginationService.setNextPageMethod(this.getPage.bind(this));
    });
  }

  private getValidProductList(rawList: Product[]): ProductCard[] {
    return rawList.map((product) => new ProductCard(product, this.storeDetail));
  }

  onPaginationChange(e) {
    this.pageSize = e.pageSize;
    this.pageOffset = e.pageIndex * e.pageSize;
    this.scrollToTop();
    this.getProductList();
  }

  private scrollToTop() {
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }

  getQueryParamByCategory() {
    if (this.countryId) {
      return {
        country: this.countryId,
      };
    }
  }

  getCategoryUrlFriendlyName(categoryName: string) {
    return categoryName.replace(/[^a-zA-Z0-9]+/g, '-');
  }

  getQueryParamByRegion(regionId, isGlobal) {
    if (regionId) {
      if (this.searchParam) {
        if (this.categoryId) {
          return {
            country: regionId,
            search: this.searchParam,
          };
        } else {
          return { country: regionId, search: this.searchParam };
        }
      } else {
        if (this.categoryId) {
          return {
            country: regionId,
          };
        } else {
          return { country: regionId };
        }
      }
    }
  }

  formListener() {
    this.sortType.valueChanges.subscribe((sort) => {
      this.getProductList();
    });
    this.discountFilter.valueChanges.subscribe((status) => {
      this.getProductList();
    });
  }

  showHideMenu() {
    this.showMobileMenu = !this.showMobileMenu;
    this.renderer[this.showMobileMenu ? 'addClass' : 'removeClass'](
      this.menuCursor.nativeElement,
      'active-category-pointer'
    );
    this.renderer[this.showMobileMenu ? 'addClass' : 'removeClass'](
      this.mobileMenu.nativeElement,
      'active-sub-menu-mobile'
    );
  }

  initForm() {
    this.brandForm = new FormGroup({
      brand: new FormControl('', [Validators.required]),
    });
  }

  onSubmitSearch() {
    if (this.brandForm.invalid) {
      return;
    }
    let queryParams;
    if (this.categoryId) {
      queryParams = {
        search: this.brandForm.get('brand').value,
      };
    } else {
      queryParams = {
        search: this.brandForm.get('brand').value,
      };
    }
    this.router.navigate(['/category'], { queryParams });
    this.brandForm.get('brand').patchValue('');
  }

  ngOnDestroy(): void {
    this.seoScriptService.removeJsonLd('store-category-schema');
  }
}
